import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { PublicHeader, PublicFooter } from "@/components/PublicShell";
import { CheckCircle, Heart, DownloadSimple, CaretLeft } from "@phosphor-icons/react";

export default function DonationSuccess() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [donation, setDonation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchById = async () => {
      if (!id) return;
      const { data } = await api.get(`/donations/${id}`);
      if (data.status === "pending" && data.gateway === "cashfree") {
        try { await api.post("/donations/cashfree/verify", { order_id: data.order_id }); } catch {}
        const refreshed = await api.get(`/donations/${id}`);
        if (!cancelled) setDonation(refreshed.data);
      } else {
        if (!cancelled) setDonation(data);
        // Safety net: razorpay verify runs synchronously now, but on a slow network or fast nav
        // there's a race. If we loaded a pending razorpay donation, poll a few times.
        if (data.status === "pending" && data.gateway === "razorpay") {
          for (let i = 0; i < 4 && !cancelled; i++) {
            await new Promise((r) => setTimeout(r, 1500));
            const again = await api.get(`/donations/${id}`);
            if (!cancelled && again.data.status !== "pending") {
              setDonation(again.data);
              break;
            }
          }
        }
      }
    };
    fetchById().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, orderId]);

  const base = process.env.REACT_APP_BACKEND_URL;
  const donationPdfUrl = donation ? `${base}/api/donations/${donation.id}/receipt.pdf?type=donation` : "#";
  const taxPdfUrl = donation ? `${base}/api/donations/${donation.id}/receipt.pdf?type=80g` : "#";

  return (
    <>
      <PublicHeader />
      <section className="max-w-2xl mx-auto px-5 md:px-10 py-16 text-center">
        {loading ? (
          <div className="text-[#56624F]">Confirming your donation...</div>
        ) : donation && donation.status === "success" ? (
          <div className="fade-up">
            <div className="w-20 h-20 rounded-full bg-[#E6F3DC] text-[#2F7A4A] grid place-items-center mx-auto mb-6">
              <CheckCircle size={48} weight="fill" />
            </div>
            <h1 className="font-display font-bold text-3xl md:text-5xl mb-3" data-testid="success-heading">Thank you, {donation.donor_name}!</h1>
            <p className="text-[#56624F] text-lg mb-8">Your donation of <span className="text-[#4FAC39] font-bold">{formatINR(donation.amount)}</span>{donation.monthly ? "/month" : ""} has been received.</p>

            <div className="seva-card text-left space-y-2 max-w-md mx-auto">
              <div className="flex justify-between"><span className="text-[#56624F]">Receipt No.</span><span className="font-mono font-semibold">{(donation.receipt_no || donation.id.slice(0, 12)).toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-[#56624F]">Campaign</span><span className="text-right">{donation.campaign_title}</span></div>
              <div className="flex justify-between"><span className="text-[#56624F]">Gateway</span><span className="uppercase">{donation.gateway}</span></div>
              <div className="flex justify-between"><span className="text-[#56624F]">Payment ID</span><span className="font-mono text-xs">{donation.payment_id || donation.order_id}</span></div>
              {donation.monthly && <div className="flex justify-between"><span className="text-[#56624F]">Frequency</span><span className="text-[#4FAC39] font-semibold">Monthly Donation</span></div>}
            </div>

            {/* Receipt downloads */}
            <div className="mt-6 flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto">
              <a href={donationPdfUrl} target="_blank" rel="noopener noreferrer" download className="btn-secondary flex-1 inline-flex items-center justify-center gap-2" data-testid="download-ack-receipt">
                <DownloadSimple size={18} weight="bold" /> Acknowledgement Receipt
              </a>
              {donation.claim_80g && (
                <a href={taxPdfUrl} target="_blank" rel="noopener noreferrer" download className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="download-80g-receipt">
                  <DownloadSimple size={18} weight="bold" /> 80G Tax Receipt
                </a>
              )}
            </div>

            {donation.donor_email && donation.donor_email !== "anonymous@seva-animals.org" && (
              <p className="text-sm text-[#2F7A4A] mt-5">A copy has also been emailed to <strong>{donation.donor_email}</strong>.</p>
            )}

            <div className="mt-8">
              {donation.campaign_slug ? (
                <Link to={`/campaigns/${donation.campaign_slug}`} className="btn-secondary inline-flex items-center gap-2" data-testid="back-to-campaign">
                  <CaretLeft size={16} /> Back to Campaign
                </Link>
              ) : (
                <Link to="/" className="btn-secondary inline-flex items-center gap-2" data-testid="back-to-home">
                  <CaretLeft size={16} /> Back to Home
                </Link>
              )}
            </div>
          </div>
        ) : donation ? (
          <div>
            <h1 className="font-display font-bold text-3xl mb-3">Payment Pending</h1>
            <p className="text-[#56624F]">We&apos;re still confirming your payment. This may take up to a minute. Refresh this page in a moment.</p>
          </div>
        ) : (
          <div>
            <h1 className="font-display font-bold text-3xl mb-3">Donation not found</h1>
            <Link to="/" className="btn-primary inline-flex mt-4">Go Home</Link>
          </div>
        )}
        <div className="mt-10 flex items-center justify-center gap-2 text-[#56624F] text-sm">
          <Heart size={18} weight="fill" className="text-[#4FAC39]" /> Every rupee heals
        </div>
      </section>
      <PublicFooter />
    </>
  );
}
